"use client";
import { useTranslations } from "next-intl";
import React, { forwardRef, useEffect, useState } from "react";
import Dropzone from "react-dropzone";
import { getDropFiles, preserveBatchLimit } from "@/lib/upload/file-drop";
import { readMediaMetadata, useObjectUrl } from "@/lib/upload/media-metadata";
import { cn } from "@/lib/utils";

export const MediaInput = ({
  onChange,
  dragOverText,
  accept = { "video/*": [], "audio/*": [] },
  multiple = false,
  maxFiles,
  maxSize,
  onReject,
  disabled = false,
  ...props
}: Omit<React.HTMLProps<HTMLDivElement>, "accept" | "onChange"> & {
  onChange: (files: File[]) => void;
  dragOverText?: string;
  accept?: Record<string, string[]>;
  multiple?: boolean;
  maxFiles?: number;
  maxSize?: number;
  disabled?: boolean;
  onReject?: (
    rejections: readonly {
      file: File;
      errors: readonly { code: string; message: string }[];
    }[]
  ) => void;
}) => {
  const t = useTranslations();
  if (!dragOverText) {
    dragOverText = t("forms.dragOverText");
  }
  return (
    <div {...props}>
      <Dropzone
        getFilesFromEvent={getDropFiles}
        noPaste
        onDrop={(accepted, rejected) => {
          const selection = preserveBatchLimit(accepted, rejected);
          if (selection.accepted.length > 0) onChange(selection.accepted);
          if (selection.rejected.length > 0) onReject?.(selection.rejected);
        }}
        accept={accept}
        multiple={multiple}
        maxFiles={multiple ? maxFiles : 1}
        maxSize={maxSize}
        disabled={disabled}
      >
        {({ getRootProps, getInputProps, isDragActive }) => (
          <section>
            <div {...getRootProps()}>
              <input {...getInputProps()} />
              <div
                className={cn({
                  "w-full border-2 border-dashed border-foreground/20 px-4 py-16 font-sans rounded-xl flex flex-col items-center justify-center text-foreground text-xl transition-colors": true,
                  "bg-card hover:border-primary/50 hover:bg-accent/40": !disabled,
                  "opacity-50 cursor-not-allowed bg-card": disabled,
                  "border-primary/70 bg-accent/50": isDragActive,
                })}
              >
                <p className="mb-4">{dragOverText}</p>
                <p className="text-base text-foreground/80">{t("forms.dropZoneDescription")}</p>
              </div>
            </div>
          </section>
        )}
      </Dropzone>
    </div>
  );
};

type MediaPreviewProps = {
  url: string;
  type: string;
} & React.HTMLProps<HTMLVideoElement | HTMLAudioElement | HTMLDivElement>;
export const MediaPreview = forwardRef<HTMLVideoElement | HTMLAudioElement, MediaPreviewProps>(
  ({ url, type, className, ...props }, ref) => {
    const t = useTranslations();

    if (type.startsWith("video")) {
      return (
        <video
          className={cn("w-full max-w-md max-h-64", className)}
          ref={ref as React.Ref<HTMLVideoElement>}
          controls
          {...props}
        >
          <source src={url} type={type}></source>
        </video>
      );
    } else if (type.startsWith("audio")) {
      return (
        <audio
          className={cn("w-full max-w-md max-h-64", className)}
          ref={ref as React.Ref<HTMLAudioElement>}
          controls
          {...props}
        >
          <source src={url} type={type}></source>
        </audio>
      );
    } else {
      return (
        <div className={className} {...props}>
          {t("uploadForm.cannotPreviewMessage")}
        </div>
      );
    }
  }
);
MediaPreview.displayName = "MediaPreview";

export const useMediaPreview = ({ file }: { file?: File }) => {
  const url = useObjectUrl(file);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    if (!file) {
      setDuration(0);
      return;
    }
    let cancelled = false;
    readMediaMetadata(file)
      .then((meta) => {
        if (!cancelled) setDuration(meta.durationS);
      })
      .catch(() => {
        if (!cancelled) setDuration(0);
      });
    return () => {
      cancelled = true;
    };
  }, [file]);

  return {
    duration,
    sizeMb: file ? file.size / 1024 / 1024 : 0,
    type: file?.type ?? "",
    url: url ?? "",
  };
};
